import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageSquare, Plus, Edit, Trash2, Save, X, Star, Search, Filter, User, Building, Calendar, Eye, EyeOff, Play,
  CheckSquare, Square, Zap, Loader2, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { testimonialAPI } from '../../services/api';
import FileUpload from '../Upload/FileUpload';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const TestimonialsManagement = () => {
  const { notify } = useNotificationHook();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  
  // Bulk actions state
  const [selectedTestimonials, setSelectedTestimonials] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    position: '',
    company: '',
    email: '',
    phone: '',
    testimonial: '',
    rating: 5,
    project_name: '',
    image: '',
    status: 'approved',
    featured: false,
    date: new Date().toISOString().split('T')[0],
    location: ''
  });

  const handleInputChange = (field, value) => {
    console.log('🔍 handleInputChange called:', field, value);
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      console.log('🔍 New formData:', newData);
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 Current formData state:', JSON.stringify(formData, null, 2));
    console.log('🔍 Image field value:', formData.image);
    let success = false;
    
    if (editingTestimonial) {
      success = await updateTestimonial(editingTestimonial.id, formData);
    } else {
      success = await createTestimonial(formData);
    }
    
    if (success) {
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', position: '', company: '', email: '', phone: '', testimonial: '', rating: 5, project_name: '', image: '', status: 'approved', featured: false, date: new Date().toISOString().split('T')[0], location: ''
    });
    setEditingTestimonial(null);
    setShowModal(false);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name || '',
      position: testimonial.position || '',
      company: testimonial.company || '',
      email: testimonial.email || '',
      phone: testimonial.phone || '',
      testimonial: testimonial.testimonial || '',
      rating: testimonial.rating || 5,
      project_name: testimonial.project_name || testimonial.project || '',
      image: testimonial.image || '',
      status: testimonial.status || 'approved',
      featured: testimonial.featured || false,
      date: testimonial.date || new Date().toISOString().split('T')[0],
      location: testimonial.location || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      await deleteTestimonial(id);
    }
  };

  const toggleStatus = async (id) => {
    const testimonial = testimonials.find(t => t.id === id);
    const newStatus = testimonial.status === 'approved' ? 'pending' : 'approved';
    await updateTestimonialStatus(id, newStatus);
  };

  const toggleFeatured = async (id) => {
    try {
      const testimonial = testimonials.find(t => t.id === id);
      const response = await testimonialAPI.update(id, { featured: !testimonial.featured });
      if (response.data.success) {
        await fetchTestimonials();
      } else {
        setError('Failed to update featured status');
      }
    } catch (err) {
      setError('Error updating featured status');
      console.error('Error updating featured status:', err);
    }
  };

  // API Functions
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await testimonialAPI.getAll({
        search: searchTerm,
        status: filterStatus === 'all' ? '' : filterStatus,
        rating: filterRating === 'all' ? '' : filterRating
      });
      
      console.log('🔍 Fetch testimonials response:', response?.data);
      
      if (response?.data?.success) {
        const testimonialsData = response.data.data.testimonials || [];
        setTestimonials(testimonialsData.map(testimonial => ({
          ...testimonial,
          featured: Boolean(testimonial.featured)
        })));
      } else {
        console.error('API response not successful:', response);
        setError('Failed to fetch testimonials');
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError(`Error loading testimonials: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createTestimonial = async (testimonialData) => {
    try {
      console.log('🔍 Creating testimonial with data:', testimonialData);
      const response = await testimonialAPI.create(testimonialData);
      console.log('🔍 Create response:', response.data);
      if (response.data.success) {
        await fetchTestimonials();
        return true;
      } else {
        setError('Failed to create testimonial');
        return false;
      }
    } catch (err) {
      setError('Error creating testimonial');
      console.error('Error creating testimonial:', err);
      return false;
    }
  };

  const updateTestimonial = async (id, testimonialData) => {
    try {
      console.log('🔍 Updating testimonial with data:', JSON.stringify(testimonialData, null, 2));
      console.log('🔍 Image field in testimonialData:', testimonialData.image);
      const response = await testimonialAPI.update(id, testimonialData);
      console.log('🔍 Update response:', response.data);
      if (response.data.success) {
        await fetchTestimonials();
        return true;
      } else {
        setError('Failed to update testimonial');
        return false;
      }
    } catch (err) {
      setError('Error updating testimonial');
      console.error('Error updating testimonial:', err);
      return false;
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      const response = await testimonialAPI.delete(id);
      if (response.data.success) {
        await fetchTestimonials();
        return true;
      } else {
        setError('Failed to delete testimonial');
        return false;
      }
    } catch (err) {
      setError('Error deleting testimonial');
      console.error('Error deleting testimonial:', err);
      return false;
    }
  };

  const updateTestimonialStatus = async (id, status) => {
    try {
      const response = await testimonialAPI.update(id, { status });
      if (response.data.success) {
        await fetchTestimonials();
        return true;
      } else {
        setError('Failed to update testimonial status');
        return false;
      }
    } catch (err) {
      setError('Error updating testimonial status');
      console.error('Error updating testimonial status:', err);
      return false;
    }
  };

  // Load testimonials on component mount and when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTestimonials();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, filterRating]);

  // Debug formData changes
  useEffect(() => {
    console.log('🔍 FormData changed:', formData);
  }, [formData]);

  // Fetch testimonials on component mount
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index} className={`h-4 w-4 ${index < rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} />
    ));
  };

  // Filter testimonials based on search and filters
  const filteredTestimonials = testimonials.filter(testimonial => {
    const matchesSearch = testimonial.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.testimonial?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || testimonial.status === filterStatus;
    const matchesRating = filterRating === 'all' || testimonial.rating === parseInt(filterRating);
    
    return matchesSearch && matchesStatus && matchesRating;
  });

  // Bulk action functions
  const handleSelectTestimonial = (testimonialId) => {
    setSelectedTestimonials(prev => 
      prev.includes(testimonialId) 
        ? prev.filter(id => id !== testimonialId)
        : [...prev, testimonialId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTestimonials([]);
      setSelectAll(false);
    } else {
      setSelectedTestimonials(testimonials.map(testimonial => testimonial.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedTestimonials.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await testimonialAPI.bulkUpdateStatus(selectedTestimonials, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedTestimonials.length} testimonials to ${status} status`);
        setSelectedTestimonials([]);
        setSelectAll(false);
        await fetchTestimonials();
      } else {
        notify.error('Failed to update testimonials: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating testimonials:', error);
      notify.error('Error updating testimonials: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkFeaturedUpdate = async (featured) => {
    if (selectedTestimonials.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await testimonialAPI.bulkUpdateFeatured(selectedTestimonials, featured);
      
      if (response.data.success) {
        notify.success(`Successfully ${featured ? 'set' : 'removed'} featured status for ${selectedTestimonials.length} testimonials`);
        setSelectedTestimonials([]);
        setSelectAll(false);
        await fetchTestimonials();
      } else {
        notify.error('Failed to update testimonials: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating testimonials:', error);
      notify.error('Error updating testimonials: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTestimonials.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedTestimonials.length} testimonials? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await testimonialAPI.bulkDelete(selectedTestimonials);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedTestimonials.length} testimonials`);
        setSelectedTestimonials([]);
        setSelectAll(false);
        await fetchTestimonials();
      } else {
        notify.error('Failed to delete testimonials: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting testimonials:', error);
      notify.error('Error deleting testimonials: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedTestimonials.length > 0);
  }, [selectedTestimonials]);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Testimonials Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage client testimonials and reviews</p>
          </div>
          <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
            <Plus className="h-4 w-4 mr-2" />Add Testimonial
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input type="text" placeholder="Search testimonials..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" />
          </div>
        </div>
        <button
          onClick={handleSelectAll}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
        >
          {selectAll ? <CheckSquare className="h-4 w-4 mr-2" /> : <Square className="h-4 w-4 mr-2" />}
          Select All
        </button>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white">
          <option value="all">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white">
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedTestimonials.length} testimonial{selectedTestimonials.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('approved')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
                  Approve
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('pending')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <EyeOff className="h-3 w-3 mr-1" />}
                  Mark Pending
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('rejected')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                  Reject
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Set Featured
                </button>
                
                <button
                  onClick={() => handleBulkFeaturedUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className="h-3 w-3 mr-1" />}
                  Remove Featured
                </button>
              </div>
            </div>
            
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
            >
              {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
        {filteredTestimonials.map((testimonial) => (
          <div key={testimonial.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => handleSelectTestimonial(testimonial.id)}
                  className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 mr-2"
                >
                  {selectedTestimonials.includes(testimonial.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    {testimonial.image ? (
                      <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.position}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.company}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {testimonial.featured && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    testimonial.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                    testimonial.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                    'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {testimonial.status}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center mb-2">
                  {renderStars(testimonial.rating)}
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">({testimonial.rating}/5)</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm italic">"{testimonial.testimonial}"</p>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                                          <span className="text-gray-500 dark:text-gray-400">Project:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{testimonial.project_name}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Date:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{new Date(testimonial.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Location:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{testimonial.location}</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <button onClick={() => toggleStatus(testimonial.id)} className={`p-1 rounded ${testimonial.status === 'approved' ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20' : 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'}`} title={testimonial.status === 'approved' ? 'Mark as pending' : 'Approve'}>
                    {testimonial.status === 'approved' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button onClick={() => toggleFeatured(testimonial.id)} className={`p-1 rounded ${testimonial.featured ? 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`} title={testimonial.featured ? 'Remove from featured' : 'Add to featured'}>
                    <Star className={`h-4 w-4 ${testimonial.featured ? 'fill-current' : ''}`} />
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => handleEdit(testimonial)} className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded" title="Edit">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(testimonial.id)} className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                </h2>
                <button onClick={resetForm} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Position *</label>
                    <input type="text" value={formData.position} onChange={(e) => handleInputChange('position', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company *</label>
                    <input type="text" value={formData.company} onChange={(e) => handleInputChange('company', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project</label>
                    <input type="text" value={formData.project_name} onChange={(e) => handleInputChange('project_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                    <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Testimonial *</label>
                  <textarea value={formData.testimonial} onChange={(e) => handleInputChange('testimonial', e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" placeholder="What did the client say about your work?" required />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating *</label>
                    <select value={formData.rating} onChange={(e) => handleInputChange('rating', parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" required>
                      <option value={5}>5 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={2}>2 Stars</option>
                      <option value={1}>1 Star</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white">
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                    <input type="date" value={formData.date} onChange={(e) => handleInputChange('date', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <input type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" placeholder="City, Country" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profile Image/Animation</label>
                  <div className="space-y-4">
                    {/* Current Image Preview */}
                    {formData.image && (
                      <div className="flex items-center space-x-4">
                        <div className="text-xs text-gray-500 mb-2">Image URL: {formData.image}</div>
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                          {formData.image.toLowerCase().endsWith('.json') ? (
                            <div className="text-center">
                              <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-1">
                                <Play className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Lottie</p>
                            </div>
                          ) : formData.image.toLowerCase().endsWith('.gif') ? (
                            <img 
                              src={formData.image} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.log('🔍 Image failed to load in admin panel:', formData.image);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <img 
                              src={formData.image} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.log('🔍 Image failed to load in admin panel:', formData.image);
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleInputChange('image', '')}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove Image
                        </button>
                      </div>
                    )}
                    
                    {/* Image Upload */}
                    <FileUpload
                      multiple={false}
                      accept="image/*,application/json,.gif,.json"
                      maxSize={5 * 1024 * 1024} // 5MB
                      maxFiles={1}
                      uploadType="animation"
                      onUploadComplete={(files) => {
                        if (files.length > 0) {
                          console.log('🔍 Upload completed, image URL:', files[0].url);
                          console.log('🔍 Setting image in formData:', files[0].url);
                          handleInputChange('image', files[0].url);
                          console.log('🔍 handleInputChange called with image:', files[0].url);
                          
                          // Force a small delay to ensure state is updated
                          setTimeout(() => {
                            console.log('🔍 FormData after upload (delayed):', formData);
                          }, 100);
                        }
                      }}
                      onUploadError={(errors) => {
                        console.error('Upload errors:', errors);
                        // You can add a toast notification here
                      }}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input type="checkbox" checked={formData.featured} onChange={(e) => handleInputChange('featured', e.target.checked)} className="mr-2" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Featured Testimonial</span>
                </div>

                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Cancel</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
                    <Save className="h-4 w-4 mr-2" />{editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default TestimonialsManagement;
